// src/pages/admin/AdminMerchantsManagement.js
import React, { useState, useEffect, useRef } from 'react';
import {
  FiShoppingBag,
  FiSearch,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiEye,
  FiAward,
  FiTrendingUp,
  FiUsers,
  FiPackage,
  FiStar,
  FiChevronLeft,
  FiChevronRight,
  FiTrash2
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';

const AdminMerchantsManagement = () => {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mountedRef = useRef(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  const merchantLevels = {
    1: { name: 'Nouveau', color: 'gray', maxDeals: 10, commission: 20 },
    2: { name: 'Bronze', color: 'orange', maxDeals: 50, commission: 18 },
    3: { name: 'Silver', color: 'gray', maxDeals: 100, commission: 15 },
    4: { name: 'Gold', color: 'yellow', maxDeals: 200, commission: 12 },
    5: { name: 'Platinum', color: 'purple', maxDeals: 999, commission: 10 }
  };

  useEffect(() => {
    mountedRef.current = true;
    fetchMerchants();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const fetchMerchants = async () => {
    if (mountedRef.current) setLoading(true);
    try {
      const res = await API.get('/admin/merchants');
      const data = res?.data;
      const list = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : data?.data || [];
      if (mountedRef.current) setMerchants(list);
      return list;
    } catch (error) {
      toast.error('Erreur lors du chargement des marchands');
      if (mountedRef.current) setMerchants([]);
      return [];
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleApprove = async (merchantId) => {
    if (!merchantId || isSubmitting) return;
    if (!window.confirm('Approuver ce marchand ?')) return;
    setIsSubmitting(true);
    try {
      await API.put(`/admin/merchants/${merchantId}/approve`);
      toast.success('Marchand approuvé');
      setMerchants((prev) =>
        prev.map((m) =>
          m._id === merchantId || m.id === merchantId
            ? { ...m, status: 'active', verified: true }
            : m
        )
      );
      setSelectedMerchant((prev) =>
        prev && prev._id === merchantId
          ? { ...prev, status: 'active', verified: true }
          : prev
      );
    } catch (error) {
      toast.error("Erreur lors de l'approbation");
      fetchMerchants();
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const handleSuspend = async (merchantId) => {
    if (!merchantId || isSubmitting) return;
    if (!window.confirm('Êtes-vous sûr de vouloir suspendre ce marchand?')) return;
    setIsSubmitting(true);
    try {
      await API.put(`/admin/merchants/${merchantId}/suspend`);
      toast.success('Marchand suspendu');
      setMerchants((prev) =>
        prev.map((m) =>
          m._id === merchantId || m.id === merchantId
            ? { ...m, status: 'suspended' }
            : m
        )
      );
      setSelectedMerchant((prev) =>
        prev && prev._id === merchantId
          ? { ...prev, status: 'suspended' }
          : prev
      );
    } catch (error) {
      toast.error('Erreur lors de la suspension');
      await fetchMerchants();
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const handleDelete = async (merchantId) => {
    if (!merchantId || isSubmitting) return;
    if (
      !window.confirm(
        'Supprimer définitivement ce marchand ? Cette action est irréversible.'
      )
    )
      return;
    setIsSubmitting(true);
    try {
      await API.delete(`/admin/merchants/${merchantId}`);
      toast.success('Marchand supprimé');
      setMerchants((prev) =>
        prev.filter((m) => !(m._id === merchantId || m.id === merchantId))
      );
      if (
        selectedMerchant &&
        (selectedMerchant._id === merchantId || selectedMerchant.id === merchantId)
      ) {
        setShowDetailsModal(false);
        setSelectedMerchant(null);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
      await fetchMerchants();
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const handleLevelChange = async (merchantId, newLevel) => {
    if (!merchantId || isSubmitting) return;
    const levelData = merchantLevels[newLevel];
    if (!levelData) {
      toast.error('Niveau invalide');
      return;
    }
    if (!window.confirm(`Changer le niveau à ${levelData.name}?`)) return;
    setIsSubmitting(true);
    try {
      await API.patch(`/admin/merchants/${merchantId}/level`, { level: newLevel });
      toast.success('Niveau mis à jour');
      setMerchants((prev) =>
        prev.map((m) =>
          m._id === merchantId || m.id === merchantId
            ? { ...m, level: newLevel }
            : m
        )
      );
      setSelectedMerchant((prev) =>
        prev && prev._id === merchantId ? { ...prev, level: newLevel } : prev
      );
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
      await fetchMerchants();
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const calculatePerformanceScore = (merchant = {}) => {
    const dealsScore = Math.min((merchant.dealsCount || 0) * 2, 30);
    const salesScore = Math.min((merchant.totalSales || 0) / 100, 30);
    const ratingScore = Math.min((merchant.rating || 0) * 8, 30);
    const verifiedBonus = merchant.verified ? 10 : 0;
    return Math.min(
      Math.round(dealsScore + salesScore + ratingScore + verifiedBonus),
      100
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      suspended: 'bg-red-100 text-red-800'
    };
    const labels = {
      active: 'Actif',
      pending: 'En attente',
      suspended: 'Suspendu'
    };
    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-semibold ${
          styles[status] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {labels[status] || status || '—'}
      </span>
    );
  };

  const getLevelBadge = (level) => {
    const levelData = merchantLevels[level] || merchantLevels[1];
    const colors = {
      gray: 'bg-gray-100 text-gray-800 border-gray-300',
      orange: 'bg-orange-100 text-orange-800 border-orange-300',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      purple: 'bg-purple-100 text-purple-800 border-purple-300'
    };
    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-semibold border ${
          colors[levelData.color] || colors.gray
        }`}
      >
        <FiAward className="inline mr-1" aria-hidden="true" />
        {levelData.name}
      </span>
    );
  };

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, levelFilter, pageSize]);

  const filteredMerchants = merchants.filter((merchant) => {
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !term ||
      String(merchant?.businessName || '')
        .toLowerCase()
        .includes(term) ||
      String(merchant?.userId?.email || '').toLowerCase().includes(term);
    const matchesStatus =
      statusFilter === 'all' || merchant?.status === statusFilter;
    const matchesLevel =
      levelFilter === 'all' ||
      (merchant?.level || 1) === parseInt(levelFilter, 10);
    return matchesSearch && matchesStatus && matchesLevel;
  });

  const totalItems = filteredMerchants.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedMerchants = filteredMerchants.slice(startIndex, endIndex);

  const stats = {
    total: merchants.length,
    pending: merchants.filter((m) => m.status === 'pending').length,
    active: merchants.filter((m) => m.status === 'active').length,
    suspended: merchants.filter((m) => m.status === 'suspended').length,
    platinum: merchants.filter((m) => (m.level || 1) === 5).length,
    verified: merchants.filter((m) => m.verified).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Gestion des Marchands
            </h1>
            <p className="text-gray-600 mt-1">
              Système de graduation style Groupon
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher marchand..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label="Rechercher marchand"
                />
              </div>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Filtrer par statut"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="pending">En attente</option>
              <option value="suspended">Suspendus</option>
            </select>

            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Filtrer par niveau"
            >
              <option value="all">Tous les niveaux</option>
              <option value="1">Niveau 1 - Nouveau</option>
              <option value="2">Niveau 2 - Bronze</option>
              <option value="3">Niveau 3 - Silver</option>
              <option value="4">Niveau 4 - Gold</option>
              <option value="5">Niveau 5 - Platinum</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FiShoppingBag
              className="text-blue-500 text-2xl"
              aria-hidden="true"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">En Attente</p>
              <p className="text-2xl font-bold text-yellow-600">
                {stats.pending}
              </p>
            </div>
            <FiAlertCircle
              className="text-yellow-500 text-2xl"
              aria-hidden="true"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Actifs</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.active}
              </p>
            </div>
            <FiCheckCircle
              className="text-green-500 text-2xl"
              aria-hidden="true"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Suspendus</p>
              <p className="text-2xl font-bold text-red-600">
                {stats.suspended}
              </p>
            </div>
            <FiXCircle
              className="text-red-500 text-2xl"
              aria-hidden="true"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Platinum</p>
              <p className="text-2xl font-bold text-purple-600">
                {stats.platinum}
              </p>
            </div>
            <FiAward
              className="text-purple-500 text-2xl"
              aria-hidden="true"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Vérifiés</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats.verified}
              </p>
            </div>
            <FiStar
              className="text-blue-500 text-2xl"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
          <FiAward className="mr-2 text-primary-600" aria-hidden="true" />
          Système de Graduation des Marchands
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-sm">
          {Object.entries(merchantLevels).map(([level, data]) => (
            <div key={level} className="bg-white rounded p-2">
              <div className="font-semibold text-gray-900">{data.name}</div>
              <div className="text-gray-600 text-xs mt-1">
                Max: {data.maxDeals} deals
                <br />
                Commission: {data.commission}%
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Marchand
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Niveau
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deals
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedMerchants.length > 0 ? (
                paginatedMerchants.map((merchant) => {
                  const score = calculatePerformanceScore(merchant);
                  const level = merchant?.level || 1;
                  return (
                    <tr
                      key={merchant._id || merchant.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <FiShoppingBag
                              className="text-primary-600"
                              aria-hidden="true"
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {merchant?.businessName || '—'}
                              {merchant?.verified && (
                                <FiCheckCircle
                                  className="inline ml-2 text-blue-500"
                                  size={14}
                                  aria-hidden="true"
                                />
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {merchant?.category || '-'}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {merchant?.userId?.firstName || ''}{' '}
                          {merchant?.userId?.lastName || ''}
                        </div>
                        <div className="text-sm text-gray-500">
                          {merchant?.userId?.email || '—'}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {getLevelBadge(level)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className="w-full bg-gray-200 rounded-full h-2 mr-2"
                            style={{ width: '100px' }}
                            aria-hidden="true"
                          >
                            <div
                              style={{ width: `${score}%` }}
                              className={`h-2 rounded-full ${
                                score >= 80
                                  ? 'bg-green-500'
                                  : score >= 60
                                  ? 'bg-yellow-500'
                                  : score >= 40
                                  ? 'bg-orange-500'
                                  : 'bg-red-500'
                              }`}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {score}/100
                          </span>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(merchant?.status)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {merchant?.dealsCount || 0} /{' '}
                        {merchantLevels[level]?.maxDeals}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedMerchant(merchant);
                              setShowDetailsModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title="Voir détails"
                            aria-label={`Voir détails ${
                              merchant?.businessName || ''
                            }`}
                          >
                            <FiEye size={18} />
                          </button>

                          {merchant?.status === 'pending' && (
                            <button
                              type="button"
                              onClick={() => handleApprove(merchant._id)}
                              className="text-green-600 hover:text-green-900"
                              title="Approuver"
                              disabled={isSubmitting}
                              aria-disabled={isSubmitting}
                            >
                              <FiCheckCircle size={18} />
                            </button>
                          )}

                          {merchant?.status === 'active' && (
                            <button
                              type="button"
                              onClick={() => handleSuspend(merchant._id)}
                              className="text-red-600 hover:text-red-900"
                              title="Suspendre"
                              disabled={isSubmitting}
                              aria-disabled={isSubmitting}
                            >
                              <FiXCircle size={18} />
                            </button>
                          )}

                          {merchant?.status === 'suspended' && (
                            <button
                              type="button"
                              onClick={() => handleDelete(merchant._id)}
                              className="text-red-600 hover:text-red-900"
                              title="Supprimer définitivement"
                              disabled={isSubmitting}
                              aria-disabled={isSubmitting}
                            >
                              <FiTrash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <FiShoppingBag
                      className="mx-auto text-4xl mb-2"
                      aria-hidden="true"
                    />
                    <p>Aucun marchand trouvé</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t bg-white">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"
            >
              <FiChevronLeft />
              <span>Préc</span>
            </button>

            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"
            >
              <span>Suiv</span>
              <FiChevronRight />
            </button>

            <div className="ml-4 text-sm text-gray-600">
              Page <strong>{page}</strong> / <strong>{totalPages}</strong>
            </div>

            <div className="ml-4 text-sm text-gray-600 flex items-center space-x-2">
              <span>Afficher</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="border px-2 py-1 rounded"
              >
                {[6, 12, 24, 48].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span>/ page</span>
            </div>
          </div>

          <div className="text-sm text-gray-600">{totalItems} résultat(s)</div>
        </div>
      </div>

      {showDetailsModal && selectedMerchant && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Profil du Marchand</h2>
                <button
                  type="button"
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Fermer"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-3">Informations Business</h3>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-gray-600">Nom:</span>{' '}
                      <span className="font-medium">
                        {selectedMerchant.businessName || '—'}
                      </span>
                    </p>
                    <p>
                      <span className="text-gray-600">Catégorie:</span>{' '}
                      {selectedMerchant.category || '—'}
                    </p>
                    <p>
                      <span className="text-gray-600">Téléphone:</span>{' '}
                      {selectedMerchant.phone || '—'}
                    </p>
                    <p>
                      <span className="text-gray-600">Adresse:</span>{' '}
                      {selectedMerchant.address || '—'}
                    </p>
                    <p>
                      <span className="text-gray-600">Statut:</span>{' '}
                      {getStatusBadge(selectedMerchant.status)}
                    </p>
                    <p>
                      <span className="text-gray-600">Vérifié:</span>{' '}
                      {selectedMerchant.verified ? '✓ Oui' : '✗ Non'}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-3">Performance</h3>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-gray-600">Niveau:</span>{' '}
                      {getLevelBadge(selectedMerchant.level || 1)}
                    </p>
                    <p>
                      <span className="text-gray-600">Score:</span>{' '}
                      <span className="font-semibold">
                        {calculatePerformanceScore(selectedMerchant)}/100
                      </span>
                    </p>
                    <p>
                      <span className="text-gray-600">Deals:</span>{' '}
                      {selectedMerchant.dealsCount || 0}
                    </p>
                    <p>
                      <span className="text-gray-600">Ventes:</span>{' '}
                      {selectedMerchant.totalSales || 0} MAD
                    </p>
                    <p>
                      <span className="text-gray-600">Note:</span>{' '}
                      {selectedMerchant.rating || 0} ⭐
                    </p>
                  </div>
                </div>

                <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-3">Changer le niveau</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(merchantLevels).map(([level, data]) => (
                      <button
                        key={level}
                        type="button"
                        onClick={() =>
                          handleLevelChange(
                            selectedMerchant._id,
                            parseInt(level, 10)
                          )
                        }
                        disabled={
                          (selectedMerchant.level || 1) ===
                            parseInt(level, 10) || isSubmitting
                        }
                        className={`px-4 py-2 rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                          data.color === 'gray'
                            ? 'bg-gray-200 hover:bg-gray-300'
                            : data.color === 'orange'
                            ? 'bg-orange-200 hover:bg-orange-300'
                            : data.color === 'yellow'
                            ? 'bg-yellow-200 hover:bg-yellow-300'
                            : 'bg-purple-200 hover:bg-purple-300'
                        }`}
                        aria-label={`Mettre au niveau ${data.name}`}
                      >
                        {data.name}
                      </button>
                    ))}
                  </div>

                  <div className="mt-4 flex items-center space-x-2">
                    {selectedMerchant.status === 'suspended' && (
                      <button
                        type="button"
                        onClick={() => handleDelete(selectedMerchant._id)}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                        disabled={isSubmitting}
                      >
                        Supprimer le compte (suspendu)
                      </button>
                    )}
                    {selectedMerchant.status === 'suspended' && (
                      <button
                        type="button"
                        onClick={() => handleApprove(selectedMerchant._id)}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                        disabled={isSubmitting}
                      >
                        Réactiver / Approuver
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMerchantsManagement;
